import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginFormData } from '../types';
import { GoogleLoginButton } from '../components/ui/GoogleLoginButton';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FormField } from '../components/ui/FormField';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ThemeToggleButton } from '../components/ui/ThemeToggleButton';
import { ThemeAwareLogo } from '../components/ui/ThemeAwareLogo';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Handle OAuth error parameters from URL
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (error && message) {
      setOauthError(decodeURIComponent(message));
      
      // Clear the error from URL without triggering a page reload
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      newSearchParams.delete('message');
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setOauthError(null);
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setFormErrors({});
    
    try {
      await login(formData);
      navigate('/app/dashboard');
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear form error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
    
    // Clear OAuth error when user starts typing
    if (oauthError) {
      setOauthError(null);
    }
  };

  const handleGoogleError = (error: string) => {
    console.error('Google login error:', error);
    setOauthError(error);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner size="lg" text="Signing you in..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-10 px-4 sm:px-6 lg:px-8 page-gradient-bg relative">
      {/* Top Bar Navigation */}
      <div className="fixed top-4 left-4 z-50">
        <Link
          to="/"
          className="inline-flex items-center text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-700 shadow-sm transition-all"
        >
          ← Back to Home
        </Link>
      </div>
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggleButton />
      </div>

      <div className="max-w-md w-full mx-auto">
        <Card className="shadow-2xl border border-gray-200/80 dark:border-gray-700/80 p-6 sm:p-8 rounded-2xl glass-card">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <ThemeAwareLogo size="xl" showText={false} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Welcome to IntFinder
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Sign in to access your intelligence workspace
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-xl p-3.5 text-rose-700 dark:text-rose-300 text-sm flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            {oauthError && (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-xl p-3.5 text-rose-700 dark:text-rose-300 text-sm flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{oauthError}</span>
              </div>
            )}
            
            <FormField
              label="Email Address"
              htmlFor="email"
              required
              error={formErrors.email}
            >
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
                placeholder="name@company.com"
                disabled={isLoading}
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor="password"
              required
              error={formErrors.password}
            >
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="input-field"
                placeholder="••••••••"
                disabled={isLoading}
              />
            </FormField>

            <Button
              type="submit"
              variant="primary"
              className="w-full py-3 rounded-xl font-semibold shadow-lg shadow-indigo-600/25"
              loading={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In ⚡'}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider">
                <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="mb-6 flex justify-center">
            <GoogleLoginButton
              onSuccess={() => {}}
              onError={handleGoogleError}
              text="Continue with Google"
              disabled={isLoading}
            />
          </div>

          {/* Sign Up Link */}
          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline transition-colors"
              >
                Create free account
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}; 